// Deye AI-W5.1 Battery Packages — fully supplied + installed bundles.
//
// IMPORTANT (customer-facing pricing rule):
//   Every price below is the CUSTOMER OUT-OF-POCKET price AFTER the estimated
//   BSTC (Battery STC) discount, based on standard installation conditions.
//   No unit prices, inverter prices, module prices, PDU prices, smart meter
//   prices, supplier costs or internal cost breakdowns are stored or displayed
//   anywhere.
//
// Each standard stack = 1 × Deye hybrid inverter + 1 × PDU base
//   + 1–6 × Deye AI-W5.1-B battery modules (5.12 kWh each) + 1 × smart meter.
// Maximum 6 battery modules / 30.72 kWh per stack — never above.

// --- Deye AI-W5.1 hybrid inverter options (no prices shown) ---
export const DEYE_INVERTERS = [
  { id: '5kw-1p', label: '5 kW 1P', short: '5 kW Single-Phase', phase: 1, model: 'AI-W5.1-5P1-AU-B' },
  { id: '10kw-1p', label: '10 kW 1P', short: '10 kW Single-Phase', phase: 1, model: 'AI-W5.1-10P1-AU-B' },
  { id: '5kw-3p', label: '5 kW 3P', short: '5 kW Three-Phase', phase: 3, model: 'AI-W5.1-5P3-AU-B' },
  { id: '10kw-3p', label: '10 kW 3P', short: '10 kW Three-Phase', phase: 3, model: 'AI-W5.1-10P3-AU-B' },
];

// The single Deye battery module used for all standard packages.
export const DEYE_MODULE = {
  name: 'Deye AI-W5.1-B Battery Module',
  model: 'AI-W5.1-B',
  capacity: '5.12 kWh',
  chemistry: 'LiFePO4 (LFP)',
};

// PDU base units (chosen by system phase).
export const DEYE_PDU = {
  1: { name: 'PDU1 + Base DC-BUS Built-in', usedFor: 'Single Phase Systems' },
  3: { name: 'PDU3 + Base DC-BUS Built-in', usedFor: 'Three Phase Systems' },
};

// Smart meters (chosen by system phase).
export const DEYE_SMART_METER = {
  1: { name: 'Deye Smart Meter SDM120CTM-40mA / ESCT-TA16', usedFor: 'Single Phase Systems' },
  3: { name: 'Deye Smart Meter SDM630CTM-40mA / ESCT-TA16', usedFor: 'Three Phase Systems' },
};

// Pricing helper: columns in user-supplied order.
const P = (fiveOneP, tenOneP, fiveThreeP, tenThreeP) => ({
  '5kw-1p': fiveOneP,
  '10kw-1p': tenOneP,
  '5kw-3p': fiveThreeP,
  '10kw-3p': tenThreeP,
});

// Customer out-of-pocket pricing after estimated BSTC discount.
const ROWS = [
  { kwh: 5.12, modules: 1, bstcQty: 34, bstcDiscount: 1258, prices: P(6080, 6828, 6878, 7120), position: 'Entry-level / smaller homes' },
  { kwh: 10.24, modules: 2, bstcQty: 68, bstcDiscount: 2516, prices: P(6105, 6853, 6903, 7145), position: 'Popular entry upgrade' },
  { kwh: 15.36, modules: 3, bstcQty: 99, bstcDiscount: 3663, prices: P(6241, 6989, 7039, 7281), position: 'Family home' },
  { kwh: 20.48, modules: 4, bstcQty: 119, bstcDiscount: 4403, prices: P(6784, 7532, 7582, 7824), position: 'High-usage solar home' },
  { kwh: 25.60, modules: 5, bstcQty: 140, bstcDiscount: 5180, prices: P(7290, 8038, 8088, 8330), position: 'Larger home' },
  { kwh: 30.72, modules: 6, bstcQty: 154, bstcDiscount: 5698, prices: P(8055, 8803, 8853, 9095), position: 'Large battery storage' },
];

export const DEYE_RANGE_IMG = '/product-images/deye/stack-range.jpg';

const kwhToSlug = (kwh) =>
  `deye-${kwh.toFixed(2).replace('.', '-')}kwh-battery-package`;

export const DEYE_PACKAGES = ROWS.map((r) => ({
  ...r,
  slug: kwhToSlug(r.kwh),
  name: `Deye ${r.kwh.toFixed(2)} kWh Battery Package`,
  img: DEYE_RANGE_IMG,
}));

export const getDeyePackage = (slug) => DEYE_PACKAGES.find((p) => p.slug === slug);

// Lowest available out-of-pocket price.
export const startingPrice = (pkg) => Math.min(...Object.values(pkg.prices));

// All inverter options are available for every Deye package.
export const availableInverters = () => DEYE_INVERTERS;

export const fmtAUD = (n) => `$${Math.round(n).toLocaleString('en-AU')}`;
